import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IGroupRepository } from '../interfaces/IGroupRepository';
import { IError } from '../interfaces/IError';
import {
  DeleteAggregateBusinessFromGroupRequest,
  DeleteGroupRequest,
  GetGroupResponseResult,
  GroupApi,
  PostAggregateBusinessToGroupRequest,
  PostAggregateBusinessToGroupResponseResult,
  PostGroupRequest,
  PostGroupResponseResult,
  UpdateGroupNameRequest,
} from '../axios/generated/groupManage';
import { customAxiosInstance } from '../axios/axiosInstance';
import { axiosErrorHandler } from '../axios/axiosError';

/**
 * グループを永続化するリポジトリ
 */
export class GroupRepository implements IGroupRepository {
  /**
   * グループを新規作成する
   * @param postGroupRequest - グループ作成情報
   */
  async postGroup(postGroupRequest: PostGroupRequest): Promise<INetworkResult<PostGroupResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const groupApi = new GroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await groupApi.postGroup(postGroupRequest);
      switch (apiResponse.status) {
        case 200:
          const result: PostGroupResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * グループの一覧を取得する
   */
  async getGroup(): Promise<INetworkResult<GetGroupResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const groupApi = new GroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await groupApi.getGroup();
      switch (apiResponse.status) {
        case 200:
          const result: GetGroupResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * グループの名前を編集する
   * @param groupId - グループID
   * @param updateGroupNameRequest - グループ名編集情報
   */
  async updateGroupName(groupId: string, updateGroupNameRequest: UpdateGroupNameRequest): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const groupApi = new GroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await groupApi.updateGroupName(groupId, updateGroupNameRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * グループを削除する
   * @param deleteGroupRequest - グループ削除情報
   */
  async deleteGroup(deleteGroupRequest: DeleteGroupRequest): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const groupApi = new GroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await groupApi.deleteGroup(deleteGroupRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 集計をグループに追加する
   * @param postAggregateBusinessToGroupRequest - 集計追加情報
   */
  async postAggregateBusinessToGroup(
    postAggregateBusinessToGroupRequest: PostAggregateBusinessToGroupRequest
  ): Promise<INetworkResult<PostAggregateBusinessToGroupResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const groupApi = new GroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await groupApi.postAggregateBusinessToGroup(postAggregateBusinessToGroupRequest);
      switch (apiResponse.status) {
        case 200:
          const result: PostAggregateBusinessToGroupResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 集計をグループから削除する
   * @param deleteAggregateBusinessFromGroupRequest - 集計削除情報
   */
  async deleteAggregateBusinessFromGroup(
    deleteAggregateBusinessFromGroupRequest: DeleteAggregateBusinessFromGroupRequest
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const groupApi = new GroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await groupApi.deleteAggregateBusinessFromGroup(deleteAggregateBusinessFromGroupRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
